// National voter-turnout history for /electionday. Five points,
// 2008 → 2026, rendered as two Recharts <Line> series so the
// historical-vs-projected distinction is unmissable:
//
//   Solid line + filled markers  · 2008–2022 final figures from
//                                  the Electoral Commission
//   Dashed line + hollow marker  · 2026, the newsroom's working
//                                  projection from current survey
//                                  data. Edit PROJECTED_2026 below
//                                  as polls firm up.
//
// Y-axis 70–100% so the 2022 drop reads at a glance — at full 0–100
// the slope flattens out and the story disappears.

import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** Editorial projection — the single value to bump as survey
 *  estimates firm up. Lives in one place so the chart is a one-
 *  line edit. */
const PROJECTED_2026 = 83;

interface Point {
  year: number;
  turnout: number;
  /** Tag controls dot rendering AND the tooltip label. */
  kind: 'historical' | 'projected';
}

const HISTORICAL: Point[] = [
  { year: 2008, turnout: 93.3, kind: 'historical' },
  { year: 2013, turnout: 93.0, kind: 'historical' },
  { year: 2017, turnout: 92.1, kind: 'historical' },
  { year: 2022, turnout: 85.6, kind: 'historical' },
];

const PROJECTION: Point[] = [
  // Hinge point shared with the historical series so the dashed
  // segment connects cleanly from 2022's real figure into 2026.
  { year: 2022, turnout: 85.6, kind: 'historical' },
  { year: 2026, turnout: PROJECTED_2026, kind: 'projected' },
];

// All five years on the x-axis so the visible range never auto-
// crops to the shorter dashed series.
const X_TICKS = [2008, 2013, 2017, 2022, 2026];

const INK = 'var(--ink, #222)';
const MUTED = 'var(--muted, #666)';

export default function TurnoutHistoryChart() {
  return (
    <div className="electionday-turnout-chart">
      <header className="electionday-turnout-chart__head">
        <h3 className="electionday-turnout-chart__title">
          National turnout · 2008–2026
        </h3>
        <p className="electionday-turnout-chart__lede">
          The 2022 drop ended four cycles of ≥ 92% turnout. Our
          working projection for 2026, drawn from current survey
          data, is shown as a dashed segment — an estimate, not a
          fact.
        </p>
      </header>

      <div className="electionday-turnout-chart__chart">
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart
            margin={{ top: 16, right: 28, bottom: 8, left: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis
              dataKey="year"
              type="number"
              domain={[2007, 2027]}
              ticks={X_TICKS}
              tick={{ fontSize: 12, fill: MUTED }}
              tickLine={{ stroke: MUTED }}
              axisLine={{ stroke: MUTED }}
              allowDuplicatedCategory={false}
            />
            <YAxis
              domain={[70, 100]}
              ticks={[70, 75, 80, 85, 90, 95, 100]}
              tick={{ fontSize: 12, fill: MUTED }}
              tickLine={{ stroke: MUTED }}
              axisLine={{ stroke: MUTED }}
              tickFormatter={(v: number) => `${v}%`}
              width={48}
            />
            <Tooltip
              cursor={{ stroke: '#ccc', strokeWidth: 1 }}
              content={<TurnoutTooltip />}
            />
            <Legend
              verticalAlign="bottom"
              align="left"
              wrapperStyle={{
                fontSize: 11,
                fontFamily: "'Poppins', system-ui, sans-serif",
                color: MUTED,
              }}
              formatter={(value) => (
                <span style={{ color: INK }}>{value}</span>
              )}
            />

            <Line
              name="Final turnout (Electoral Commission)"
              data={HISTORICAL}
              dataKey="turnout"
              stroke={INK}
              strokeWidth={2.5}
              dot={{ r: 5, fill: INK, stroke: INK }}
              activeDot={{ r: 6, fill: INK, stroke: INK }}
              isAnimationActive={false}
            />
            <Line
              name="2026 projected (survey estimate)"
              data={PROJECTION}
              dataKey="turnout"
              stroke={MUTED}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={(props: unknown) => {
                // Recharts passes the point as `payload`; the hinge-
                // point at 2022 is also in HISTORICAL and gets drawn
                // there with a solid dot, so we render the projection
                // marker ONLY at the 2026 point — hollow circle so the
                // "estimate" framing is unmistakable.
                const p = props as {
                  cx?: number;
                  cy?: number;
                  payload?: { kind?: string; year?: number };
                  key?: string | number;
                };
                if (
                  p.payload?.kind !== 'projected' ||
                  p.cx == null ||
                  p.cy == null
                ) {
                  return <g key={p.key ?? 'noop'} />;
                }
                return (
                  <g key={p.key ?? `dot-${p.payload?.year ?? 0}`}>
                    <circle
                      cx={p.cx}
                      cy={p.cy}
                      r={6}
                      fill="#fff"
                      stroke={MUTED}
                      strokeWidth={2.5}
                    />
                    <text
                      x={p.cx + 10}
                      y={p.cy - 8}
                      fontFamily="'Poppins', system-ui, sans-serif"
                      fontSize={10}
                      fontStyle="italic"
                      fill={MUTED}
                    >
                      projected — survey estimate
                    </text>
                  </g>
                );
              }}
              activeDot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="electionday-turnout-chart__source">
        Final turnout, Electoral Commission. 2026 figure is editorial
        projection from survey data.
      </p>
    </div>
  );
}

interface TooltipPayloadItem {
  payload?: Point;
  value?: number;
}

interface TooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

function TurnoutTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  // Recharts can deliver duplicate entries when two Lines share a
  // year (our 2022 hinge); collapse to the first by year + dedupe
  // by `kind` for a clean tooltip.
  const seen = new Set<string>();
  const rows = payload
    .map((p) => p.payload)
    .filter((p): p is Point => !!p)
    .filter((p) => {
      const key = `${p.year}-${p.kind}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (rows.length === 0) return null;
  const yr = rows[0].year;
  return (
    <div className="electionday-turnout-chart__tip">
      <strong>{yr}</strong>
      {rows.map((p) => (
        <div key={`${p.year}-${p.kind}`}>
          {p.turnout.toFixed(1)}%{' '}
          <span className="electionday-turnout-chart__tip-kind">
            {p.kind === 'projected' ? '— projection' : '— final'}
          </span>
        </div>
      ))}
    </div>
  );
}
